// client/src/components/ProductList.js
import React, { useMemo, useState } from "react";
import { Container, Typography, Grid } from "@mui/material";
import ProductCard from "./ProductCard";
import ProductFilters from "./ProductFilters";
import { useCatalog } from "../hooks/useCatalog"; // fetches /api/catalog

// money helper (CAD by default)
const money = (amount, currency = "cad") =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount || 0) / 100);

export default function ProductList() {
  const { products: stripeProducts = [], loading, error } = useCatalog();

  const [filters, setFilters] = useState({ heat: [] }); // numbers like [1,2,3,4,5]

  // Map Stripe → legacy shape your ProductCard/cart expect
  const products = useMemo(() => {
    return (stripeProducts || []).map((p) => {
      const sizes = (p.sizes || []).map((s) => s.label);
      const price = {};
      const priceIdBySize = {};
      const currencyBySize = {};
      (p.sizes || []).forEach((s) => {
        if (!s?.label) return;
        price[s.label] = (Number(s.unitAmount || 0) / 100).toFixed(2);
        priceIdBySize[s.label] = s.priceId;
        currencyBySize[s.label] = s.currency;
      });
      return {
        ...p,
        sizes,
        price, // { "5oz":"7.99", "12oz":"14.99" }
        priceIdBySize, // { "5oz":"price_123", ... }
        currencyBySize, // { "5oz":"cad", ... }
        // provide a displayPrice fallback if ProductCard shows one number
        displayPrice: p.sizes?.[0]
          ? money(p.sizes[0].unitAmount, p.sizes[0].currency)
          : null,
        spiceLevel: Number(p.spiceLevel ?? 0), // metadata from Stripe is string → number
      };
    });
  }, [stripeProducts]);

  // Apply heat filter if any (matches your ProductFilters heat array)
  const filtered = useMemo(() => {
    if (!filters.heat?.length) return products;
    return products.filter((p) =>
      filters.heat.includes(Number(p.spiceLevel || 0))
    );
  }, [products, filters]);

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h5">Loading products…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h5">Failed to load products.</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
        All Products
      </Typography>

      <ProductFilters filters={filters} setFilters={setFilters} />

      <Grid container spacing={3} justifyContent="center">
        {filtered.map((p) => (
          <Grid key={p.id} size={{ xs: 12, sm: 6, md: 3 }}>
            <ProductCard product={p} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
